import { useRef } from "react";
import { SecondaryButton } from "../Button/Buttons";
import logger from "../../services/Logger";
import { mapValue, validateFileOnCreateSpectra, ALLOWED_FILE_ACCEPT_STRING } from "../../utils/helpers";
import { SPECTRA_MAX_FILE_COUNT, SPECTRA_MAX_TOTAL_SIZE_MB, SPECTRA_MAX_SINGLE_FILE_SIZE_MB } from "../../constants/fileConstants";
import { BlackCross, CrossSvg } from "../../assets";
import { useTheme } from "../../context/ThemeProvider";
import api from "../../api";
import mediaUrlAdapter from "../../api/Media/mediaUrlAdapter";
import { v4 as uuidv4 } from "uuid";

const FilePreview = ({ fileObj, index, onCancel }) => {
  const { theme } = useTheme();
  const { file, progress, isUploaded, downloadURL } = fileObj;

  const getUploadStatus = () => {
    if (isUploaded) return "✓ Uploaded";
    if (progress > 0) return `${Math.round(progress)}%`;
    return "Uploading...";
  };

  const handleDownload = () => {
    if (isUploaded && downloadURL) {
      window.open(downloadURL, '_blank');
    }
  };

  return (
    <div
      className={`
              flex items-center gap-2 px-3 border-[0.5px] py-1 rounded-full min-w-fit text-xs
              ${isUploaded
                ? 'bg-fill-green-50 border-edge-green-200 dark:bg-fill-green-900/20 dark:border-edge-green-600'
                : 'bg-fill-navigation-hover border-edge-hex-6d808d4d dark:bg-fill-hex-454e5a dark:border-edge-control-border'
              } opacity-100
              relative overflow-hidden shadow-[0_0_2px_#66666626] dark:shadow-[0_0_4px_#00000040]
            `}
    >
      {/* Filename and status */}
      <div className="flex items-center gap-2">
        <span className="text-xs max-w-[120px] truncate font-semibold text-ink-ink-alternate dark:text-ink-sidebar-text-dark opacity-100">
          {file.name}
        </span>
        <span className={`text-xs font-medium ${
          isUploaded
            ? 'text-ink-green-600 dark:text-ink-green-400'
            : 'text-ink-orange-600 dark:text-ink-hex-fb923c'
        }`}>
          {getUploadStatus()}
        </span>
      </div>

      <div className="flex items-center gap-1">
        {isUploaded && downloadURL && (
          <button
            onClick={handleDownload}
            className="text-ink-blue-500 hover:text-ink-blue-600 dark:text-ink-blue-400 dark:hover:text-ink-blue-300 transition-colors flex-shrink-0"
            aria-label={`Download ${file.name}`}
            title="Download file"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </button>
        )}
        <button
          onClick={() => onCancel(index)}
          className="text-ink-gray-400 hover:text-ink-gray-300 transition-colors flex-shrink-0"
          aria-label={`Remove ${file.name}`}
        >
          {theme === "dark" ? (
            <CrossSvg className="h-4" />
          ) : (
            <BlackCross className="h-4" />
          )}
        </button>
      </div>

      {/* Progress line at bottom - with fixed position */}
      {!isUploaded && progress > 0 && progress < 100 && (
          <div
            className="absolute bottom-0 left-0 right-0 h-[1px] bg-fill-editor-warning origin-left transition-transform duration-300 ease-in-out"
            style={{
              transform: `scaleX(${progress / 100})`,
            }}
          />
        )}
    </div>
  );
};

const FileUploadInSpectraEdit = ({
  files,
  setFiles,
  spaceId,
}) => {
  const inputRef = useRef(null);

  const handleAddFilesBtnClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target?.files || []);
    const validFiles = [];
    selectedFiles.forEach((file) => {
      const pendingFiles = validFiles.map((pendingFile) => ({ file: pendingFile }));
      if (validateFileOnCreateSpectra(file, [...files, ...pendingFiles], SPECTRA_MAX_SINGLE_FILE_SIZE_MB, SPECTRA_MAX_FILE_COUNT, SPECTRA_MAX_TOTAL_SIZE_MB)) {
        validFiles.push(file);
      }
    });

    const newFileObjects = validFiles.map((file) => ({
      file,
      uploadId: uuidv4(),
      isUploaded: false,
      progress: 0,
      abortController: new AbortController(),
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      isOriginal: false,
    }));

    setFiles((prevFiles) => [...prevFiles, ...newFileObjects]);
    e.target.value = "";

    newFileObjects.forEach(({ file, abortController, id, uploadId }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("space_id", spaceId);
      formData.append("upload_id", uploadId);

      api({
        method: "POST",
        url: "/storage/assets/upload",
        data: formData,
        signal: abortController.signal,
        onUploadProgress: (e) => {
          const progress = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          setFiles((prev) =>
            prev.map((f) =>
              f.id === id
                ? { ...f, progress: mapValue(progress, 0, 100, 0, 90) }
                : f
            )
          );
        },
      })
        .then(async (importResponse) => {
          const asset = importResponse?.data?.asset;
          if (!asset?.asset_id) throw new Error("Asset upload returned no asset_id");
          const resolvedAsset = await mediaUrlAdapter.resolveAsset(asset.asset_id);
          setFiles((prev) =>
            prev.map((f) =>
              f.id === id
                ? {
                    ...f,
                    isUploaded: true,
                    downloadURL: resolvedAsset.url,
                    progress: 100,
                    asset_id: asset.asset_id,
                  }
                : f
            )
          );
        })
        .catch((error) => {
          if (error.name === 'CanceledError' || error.name === 'AbortError') return;
          logger.error('FileUploadInSpectraEdit', `Upload failed for ${file.name}`, { error: error.message });
          setFiles((prev) => prev.filter((f) => f.id !== id));
        });
    });
  };

  const handleCancel = async (index) => {
    const fileObj = files[index];
    const { abortController, isUploaded, isOriginal } = fileObj;

    setFiles((prev) => prev.filter((_, i) => i !== index));
    inputRef.current.value = "";

    try {
      if (!isOriginal) {
        if (!isUploaded && abortController) {
          abortController.abort();
        }

      } else {
        // Existing references are removed when the edited asset list is saved.
      }
    } catch (error) {
      logger.error('FileUploadInSpectraEdit', 'Error during file cancel/delete', { error: error.message });
    }
  };

  return (
    <>
      <label htmlFor="add-files-on-create-spectra" className="cursor-pointer">
        <span
          className={`text-ink-ink dark:text-ink-white text-sm font-semibold`}
        >
          Edit Attachments
        </span>
        <span className="text-ink-hex-aaaaaa text-xs block mb-2">
          Upload up to {SPECTRA_MAX_FILE_COUNT} files, maximum upload size {SPECTRA_MAX_SINGLE_FILE_SIZE_MB} MB per file.
        </span>
      </label>
      {files.length > 0 && (
        <div className="flex items-center flex-wrap gap-2 mb-2">
          {files.map((fileObj, index) => (
            <FilePreview
              key={fileObj.id}
              fileObj={fileObj}
              index={index}
              onCancel={handleCancel}
            />
          ))}
        </div>
      )}
      <div>
        <SecondaryButton type="button" onClick={handleAddFilesBtnClick}>
          Add Files
        </SecondaryButton>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_FILE_ACCEPT_STRING}
        className="hidden"
        id="add-files-on-create-spectra"
        onChange={handleFileChange}
        multiple
      />
    </>
  );
};

export default FileUploadInSpectraEdit;
